import { numberToPrettyString } from '@cian/utils';

import { isDailyRent } from './checkOfferType';
import { getPaymentPeriod } from './getPaymentPeriod';
import { BARGAIN_TERMS_SALE_TYPE_PRESENTER, ECategory, IOfferBargainTerms, IOfferLand } from 'shared/types/offers';
import { TUser } from 'shared/types/user';

export const getBargainAllowed = (bargainTerms?: IOfferBargainTerms): string => {
  return bargainTerms?.bargainAllowed ? 'возможен торг' : '';
};

export const getSalePrice = (bargainTerms?: IOfferBargainTerms): string | null => {
  if (!bargainTerms || !bargainTerms.priceRur) {
    return null;
  }

  const formattedPrice = bargainTerms.priceRur.toLocaleString('ru');

  return `${formattedPrice} ₽`;
};

export const getSalePricePerMeter = ({
  bargainTerms,
  totalArea,
}: {
  bargainTerms?: IOfferBargainTerms;
  totalArea?: number;
}): string | null => {
  if (!bargainTerms) {
    return null;
  }

  const { priceType, priceRur } = bargainTerms;

  if (priceType !== 'all') {
    return priceRur ? `${numberToPrettyString(priceRur)} ₽` : null;
  } else if (totalArea) {
    if (totalArea > 0 && priceRur) {
      const pricePerMeter = Math.round(priceRur / totalArea);
      const pricePerMeterFormatted = numberToPrettyString(pricePerMeter);

      return `${pricePerMeterFormatted} ₽ за м\u00b2`;
    }
  }

  return null;
};

export const getRentPrice = (category: ECategory, bargainTerms?: IOfferBargainTerms): string => {
  if (!bargainTerms || !bargainTerms.priceRur) {
    return '';
  }

  let price = bargainTerms.priceRur;

  if (bargainTerms.utilitiesTerms && !bargainTerms.utilitiesTerms.includedInPrice) {
    price += bargainTerms.utilitiesTerms.price || 0;
  }

  return `${numberToPrettyString(price)} ₽/${getPaymentPeriod(category)}`;
};

export const getMoreRentPrice = (category: ECategory, bargainTerms?: IOfferBargainTerms): string | null => {
  const { utilitiesTerms } = bargainTerms || {};

  if (isDailyRent(category)) {
    return null;
  }

  if (!bargainTerms || !bargainTerms.priceRur) {
    return '';
  }

  /**
   * Если utilitiesTerms с бекенда приходит null, значит ком. платежи включены,
   * так исторически сложилось
   */
  if (utilitiesTerms === null) {
    return 'Комм. платежи включены без счётчиков';
  }

  if (utilitiesTerms) {
    if (utilitiesTerms.includedInPrice) {
      return 'Комм. платежи включены без счётчиков';
    }

    if (!utilitiesTerms.includedInPrice && utilitiesTerms.price === 0) {
      return 'Комм. платежи не включены';
    }

    if (!utilitiesTerms.includedInPrice && utilitiesTerms.price && utilitiesTerms.price > 0) {
      const priceRent = bargainTerms.priceRur;
      const priceUtilities = utilitiesTerms.price;

      return `${numberToPrettyString(priceRent)} \u20bd + ${numberToPrettyString(
        priceUtilities,
      )} \u20bd комм. платежи без счётчиков`;
    }
  }

  return '';
};

export const getClientFee = (bargainTerms?: IOfferBargainTerms): string => {
  return bargainTerms?.clientFee ? `комиссия ${bargainTerms.clientFee}%` : 'без комиссии';
};

export const getFee = (bargainTerms?: IOfferBargainTerms): string => {
  const { agentFee, clientFee } = bargainTerms || {};
  const fee: string[] = [];

  if (agentFee != null && typeof agentFee !== 'undefined') {
    fee.push(`аг.\u00A0${agentFee}%`);
  }

  if (clientFee != null && typeof clientFee !== 'undefined') {
    fee.push(`кл.\u00A0${clientFee}%`);
  }

  return fee.join(', ');
};

export const getDeposit = (bargainTerms?: IOfferBargainTerms): string => {
  if (bargainTerms?.deposit) {
    switch (bargainTerms.currency) {
      case 'eur':
        return `залог \u20AC${numberToPrettyString(bargainTerms.deposit)}`;
      case 'rur':
        return `залог ${numberToPrettyString(bargainTerms.deposit)} ₽`;
      case 'usd':
        return `залог $${numberToPrettyString(bargainTerms.deposit)}`;
    }
  }

  return 'без залога';
};

export const getCurrency = (currency: string): string => {
  switch (currency) {
    case 'eur':
      return '\u20ac';
    case 'usd':
      return '\u0024';
    case 'percent':
      return '%';
    default:
      return '\u20bd';
  }
};

export const getAgentBonus = (user: TUser, bargainTerms?: IOfferBargainTerms): string | null => {
  if (!bargainTerms) {
    return null;
  }
  const agentBonus = bargainTerms.agentBonus;

  if (user.isAuthenticated && Boolean(user.isAgent) && agentBonus) {
    const bonusCurrency =
      agentBonus.paymentType === 'fixed'
        ? getCurrency(agentBonus.currency || '')
        : getCurrency(agentBonus.paymentType || '');

    return agentBonus.value ? `Бонус агенту ${numberToPrettyString(agentBonus.value)} ${bonusCurrency}` : null;
  }

  return null;
};

export const getCommercialRentPrice = (category: ECategory, priceTotalPerMonthRur?: number): string => {
  return priceTotalPerMonthRur ? `${numberToPrettyString(priceTotalPerMonthRur)} ₽/${getPaymentPeriod(category)}` : '';
};

export const getCommercialPerMeterByYear = (pricePerUnitAreaPerYearRur?: number): string => {
  return pricePerUnitAreaPerYearRur ? `${numberToPrettyString(pricePerUnitAreaPerYearRur)} ₽ за м\u00b2 в год` : '';
};

export const getCommercialLandUnit = (land?: IOfferLand) => {
  if (!land || !land.areaUnitType) {
    return null;
  }

  return {
    text: land.areaUnitType === 'sotka' ? 'сот.' : 'га.',
    type: land.areaUnitType,
  };
};

export const getCommercialLandPerMeterByYear = ({
  pricePerUnitAreaPerYearRur,
  land,
}: {
  pricePerUnitAreaPerYearRur?: number;
  land?: IOfferLand;
}) => {
  const unit = getCommercialLandUnit(land);

  return pricePerUnitAreaPerYearRur
    ? `${numberToPrettyString(pricePerUnitAreaPerYearRur)} ₽ за ${unit ? unit.text : 'м\u00b2'} в год`
    : '';
};

export const getSaleType = (bargainTerms?: IOfferBargainTerms): string | null => {
  if (bargainTerms && bargainTerms.saleType) {
    return BARGAIN_TERMS_SALE_TYPE_PRESENTER[bargainTerms.saleType] || null;
  }

  return null;
};
